﻿import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserProvider, useUser } from "@foundation/contextapi/UserContext";
import { SubscriptionProvider } from "@foundation/contextapi/SubscriptionContext";
import { WorkspaceProvider } from "@foundation/hooks/useWorkspaceContext";
import {
  PluginProvider,
  PluginRoutes,
  PluginProviders,
} from "./plugins/loader";
import { Toaster } from "@foundation/components/ui/toaster";
import { ThemeProvider } from "@foundation/components/theme-provider";
import { supabase, setupSessionRefresh } from "@foundation/utils/supabaseClient";
import { useUserRole } from "@foundation/hooks/useUserRole";
import MainLayout from "./layout/MainLayout";
import Login from "@foundation/components/auth/Login";
import Signup from "@foundation/components/auth/Signup";
// import Dashboard from "@foundation/pages/Dashboard";
import Profile from "@foundation/pages/EnhancedProfileSettings";
import ForgotPassword from "@foundation/pages/forgot-password";
import UpdatePassword from "@foundation/pages/update-password";
import Settings from "@foundation/pages/Settings";
import { OrganizationManagePage } from "@foundation/pages/OrganizationManagePage";
import AdminPortal from "@foundation/pages/AdminPortal";
import { AdminProtectedRoute } from "@foundation/components/admin/AdminProtectedRoute";
import PendingApprovalPage from "@foundation/components/auth/PendingApprovalPage";
// ðŸš« DISABLED: FormEngine feature is disabled during plugin migration
// import FormEngineApp from "./features/formengine/FormEngineApp";
import Dashboard from "./features/revisr/pages/Dashboard";
import OnboardingWizard from "@foundation/components/onboarding/OnboardingWizard";
import { useOnboardingStatus } from "@foundation/hooks/useOnboardingStatus";
import { InviteLinkHandler } from "@foundation/components/invitation/InviteLinkHandler";
import { OnboardingBoostPolicyPage } from "./features/revisr/pages/OnboardingBoostPolicyPage";

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              The application encountered an error. Please try refreshing the
              page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
// Root redirect component with error handling
const RootRedirect = () => {
  const { user, isLoading } = useUser();

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Redirect based on auth state
  if (user) {
    return <Navigate to="/revisr" replace />;
  } else {
    return <Navigate to="/app-login" replace />;
  }
};

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useUser();
  const { canAccessApp, needsApproval, isLoading: roleLoading } = useUserRole();
  const { needsOnboarding, isLoading: onboardingLoading } = useOnboardingStatus();

  if (isLoading || roleLoading || onboardingLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // If user needs approval, show pending approval page
  if (needsApproval) {
    return <PendingApprovalPage />;
  }

  // If user cannot access app (disapproved), redirect to login
  if (!canAccessApp) {
    return <Navigate to="/app-login" replace />;
  }

    // If user needs onboarding, redirect to onboarding flow
    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }

  return <>{children}</>;
};

// Onboarding protected route component
const OnboardingProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useUser();
  const { canAccessApp, needsApproval, isLoading: roleLoading } = useUserRole();

  if (isLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user needs approval, show pending approval page
  if (needsApproval) {
    return <PendingApprovalPage />;
  }

  if (!canAccessApp) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Wrapper component for OnboardingWizard that handles status refresh
const OnboardingWizardWithRefresh = () => {
  const { refresh } = useOnboardingStatus();
  
  // Don't auto-refresh on mount to avoid interfering with auth state
  // Only refresh when onboarding is actually completed
  
  return <OnboardingWizard onComplete={refresh} />;
};

const AppRoutes = () => {
  const { user, isLoading } = useUser();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const initializeSession = async () => {
      try {
        cleanup = await setupSessionRefresh();

        const { data } = await supabase.auth.getSession();
        console.log("Current session:", data.session ? "Active" : "None");
      } catch (error) {
        console.error("Session initialization failed:", error);
      }
    };

    initializeSession();

    // Cleanup on unmount
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, []); // Empty dependency array - only run once on mount

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Root redirect - inside MainLayout so header shows during auth loading */}
      <Route path="/" element={<RootRedirect />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {/* <Dashboard /> */}
            <Navigate to="/revisr" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/organisation/:id"
        element={
          <ProtectedRoute>
            <OrganizationManagePage />
          </ProtectedRoute>
        }
      />


      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminProtectedRoute>
              <AdminPortal />
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />

            {/* Invite link handler - public route */}
            <Route path="/invite/:code" element={<InviteLinkHandler />} />

{/* Onboarding route - bypasses onboarding check */}
<Route
  path="/onboarding"
  element={
    <OnboardingProtectedRoute>
      <OnboardingWizardWithRefresh />
    </OnboardingProtectedRoute>
  }
/>

{/* Onboarding policy generation - dedicated page using BoostPolicyFlow */}
<Route
  path="/onboarding/boost-policy"
  element={
    <OnboardingProtectedRoute>
      <OnboardingBoostPolicyPage />
    </OnboardingProtectedRoute>
  }
/>

      {/* ðŸš« DISABLED: FormEngine Routes - Feature disabled during plugin migration */}
      {/*
      <Route
        path="/formengine/*"
        element={
          <ProtectedRoute>
            <AdminProtectedRoute>
              <FormEngineApp />
            </AdminProtectedRoute>
          </ProtectedRoute>
        }
      />
      */}

      {/* ðŸ†• Plugin Routes - All plugins handled dynamically (includes 404 fallback) */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <PluginRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Wrapper component to access user context
const AppRoutesWithUser = () => {
  const { user } = useUser();

  return (
    <Routes>
      {/* App authentication routes (no MainLayout) */}
      <Route
        path="/app-login"
        element={user ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="/app-signup"
        element={user ? <Navigate to="/dashboard" replace /> : <Signup />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />

      
      {/* App routes (with MainLayout) */}
      <Route
        path="/*"
        element={
          <MainLayout>
            <AppRoutes />
          </MainLayout>
        }
      />
    </Routes>
  );
};

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UserProvider>
          <SubscriptionProvider>
            <WorkspaceProvider>
            <PluginProvider>
              <PluginProviders>
                {/* ðŸš« DISABLED: CopilotKit disabled since AI Chat feature is disabled */}
                {/*
                <CopilotKit
                  runtimeUrl={`${
                    import.meta.env.VITE_API_URL || "http://localhost:3001"
                  }/api/aichat`}
                  headers={{
                    "x-api-key": import.meta.env.VITE_API_KEY || "revisr",
                  }}
                  showDevConsole={import.meta.env.DEV || false}
                >
                */}
                  <ThemeProvider storageKey="revisr-theme">
                    <div className="relative min-h-screen">
                    <AppRoutesWithUser />

                    {/* ðŸš« DISABLED: CopilotPopup disabled since AI Chat feature is disabled */}
                    {/*
                    <div className="fixed bottom-4 right-4 z-50">
                      <CopilotPopup
                        instructions="You are AltZero AI, an intelligent assistant for the AltZero platform. You can help users navigate the platform, answer questions, and provide assistance with various tasks."
                        labels={{
                          title: "AltZero Assistant",
                          initial:
                            "Hi! I'm your AltZero assistant. How can I help you today?",
                        }}
                      />
                    </div>
                    */}

                    <Toaster />
                  </div>
                </ThemeProvider>
                {/* </CopilotKit> */}
              </PluginProviders>
            </PluginProvider>
            </WorkspaceProvider>
          </SubscriptionProvider>
        </UserProvider>
      </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;


